import type { RegisterFormType, RepresentantFormData } from '@/schemas/register.forestowner'
import type { NameModel } from '@/types/Common/NameModel'
import type { PhoneCollectionModel } from '@/types/Common/PhoneCollectionModel'
import type { ContactPersonModel } from '@/types/ForestOwners/ContactPerson'
import type {
  CreateForestOwnerRequest,
  IdentificationNumberModel,
  IdentificationNumberTypeModel,
  OperatingOrganizationModel,
  OrganizationTypeModel,
} from '@/types/ForestOwners/ForestOwner'
import { parseToPhoneCollectionModel, parseToPhoneNumberModel } from '@/utils/phone'

export interface IdentificationNumberResult {
  identificationNumber: {
    type: IdentificationNumberTypeModel
    number: string
    organizationType?: OrganizationTypeModel
  } | null
  birthDate: string | undefined
}

// Robust date handling: converts various date formats to ISO format (yyyy-MM-dd) for backend

/**
 * Validates if a string is a valid YYYY-MM-DD date format
 */
function isValidISOFormat(dateString: string): boolean {
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/
  if (!isoDateRegex.test(dateString)) {
    return false
  }

  // Validate the date is actually valid
  const [year, month, day] = dateString.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
}

/**
 * Converts a Date or string to ISO format (yyyy-MM-dd) for backend
 */
function formatDateToISO(birthDate: string | Date | null | undefined): string | undefined {
  if (!birthDate) return undefined

  let birthDateString: string | null = null

  if (birthDate instanceof Date) {
    const year = birthDate.getFullYear()
    const month = (birthDate.getMonth() + 1).toString().padStart(2, '0')
    const day = birthDate.getDate().toString().padStart(2, '0')
    birthDateString = `${year}-${month}-${day}`
  } else if (typeof birthDate === 'string' && birthDate) {
    // Handle various date string formats
    if (birthDate.includes('T')) {
      // ISO datetime string - extract just the date part
      const datePart = birthDate.split('T')[0]
      if (isValidISOFormat(datePart)) {
        birthDateString = datePart
      }
    } else if (birthDate.includes('.')) {
      // Norwegian format (dd.mm.yyyy) - convert to ISO format first
      const [day, month, year] = birthDate.split('.')
      if (day && month && year) {
        const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
        if (isValidISOFormat(isoDate)) {
          birthDateString = isoDate
        }
      }
    } else if (birthDate.includes('-')) {
      // Already in ISO format (yyyy-MM-dd) - validate it
      if (isValidISOFormat(birthDate)) {
        birthDateString = birthDate
      }
    } else {
      // Unknown format - try to parse as Date
      try {
        const parsedDate = new Date(birthDate)
        if (!Number.isNaN(parsedDate.getTime())) {
          const year = parsedDate.getFullYear()
          const month = (parsedDate.getMonth() + 1).toString().padStart(2, '0')
          const day = parsedDate.getDate().toString().padStart(2, '0')
          const isoDate = `${year}-${month}-${day}`
          if (isValidISOFormat(isoDate)) {
            birthDateString = isoDate
          }
        }
      } catch {
        // Invalid date format
        birthDateString = null
      }
    }
  }

  return birthDateString ?? undefined
}

/**
 * Builds identification number and birthdate based on available data
 * Now expects full 11-digit SSN instead of 5-digit personal number
 */
export function buildIdentificationNumber(
  birthDate: string | Date | null | undefined,
  ssn: string | null | undefined,
): { identificationNumber: IdentificationNumberModel | null; birthDate: string | undefined } {
  // Convert Date to string if needed
  const birthDateString = formatDateToISO(birthDate)

  // Clean SSN (remove spaces/dashes)
  const cleanedSSN = ssn?.replace(/[\s-]/g, '') ?? null

  // If we have a valid 11-digit SSN, use it directly
  if (cleanedSSN && /^\d{11}$/.test(cleanedSSN)) {
    return {
      identificationNumber: {
        type: 'Ssn',
        number: cleanedSSN,
      },
      birthDate: birthDateString, // Always include birthDate when available
    }
  }

  // Only birthDate available (no SSN)
  if (birthDateString) {
    return {
      identificationNumber: null,
      birthDate: birthDateString,
    }
  }

  // Neither available
  return {
    identificationNumber: null,
    birthDate: undefined,
  }
}

/**
 * Builds contact person data based on mail recipient selection
 */
export function buildContactPerson(
  mailRecipient: string,
  stepData: RegisterFormType['step1Person'] | RegisterFormType['step1Organization'],
  representants?: RepresentantFormData[],
  forestOwnerIdentificationNumber?: { type: IdentificationNumberTypeModel; number: string } | null,
): {
  firstName: string
  lastName: string
  email: string | null
  phoneNumber: PhoneCollectionModel | null
  socialSecurityNumber: string | null
  birthDate?: string
} {
  switch (mailRecipient) {
    case 'forestOwner':
      return {
        firstName: stepData.firstName,
        lastName: stepData.lastName,
        email: stepData.email?.trim() ? stepData.email.trim() : null,
        phoneNumber: parseToPhoneCollectionModel(stepData.phone, true),
        socialSecurityNumber: forestOwnerIdentificationNumber?.number ?? null,
        birthDate: formatDateToISO(stepData.birthDate),
      }
    case 'representative': {
      // For private person: take first (and only) representant from array if available
      const rep = representants?.[0]
      if (rep) {
        // Build SSN for representant from birthDate and ssn
        const repIdentificationNumber = buildIdentificationNumber(rep.birthDate, rep.ssn)
        const phoneNumber = parseToPhoneCollectionModel(rep.phone, true)

        return {
          firstName: rep.firstName,
          lastName: rep.lastName,
          email: rep.email?.trim() ? rep.email.trim() : null,
          phoneNumber,
          socialSecurityNumber: repIdentificationNumber.identificationNumber?.number ?? null,
          birthDate: formatDateToISO(rep.birthDate),
        }
      }
      // Fallback to forest owner if no representant
      const phoneNumber = parseToPhoneCollectionModel(stepData.phone, true)

      return {
        firstName: stepData.firstName,
        lastName: stepData.lastName,
        email: stepData.email?.trim() ? stepData.email.trim() : null,
        phoneNumber,
        socialSecurityNumber: forestOwnerIdentificationNumber?.number ?? null,
        birthDate: formatDateToISO(stepData.birthDate),
      }
    }
    case 'organization': {
      // For organization, use organization contact person or fallback to forest owner
      const phoneNumber = parseToPhoneCollectionModel(stepData.phone, true)

      return {
        firstName: stepData.firstName,
        lastName: stepData.lastName,
        email: stepData.email?.trim() ? stepData.email.trim() : null,
        phoneNumber,
        socialSecurityNumber: forestOwnerIdentificationNumber?.number ?? null,
        birthDate: formatDateToISO(stepData.birthDate),
      }
    }
    case 'other': {
      // For "other", contactPerson should use representant information, not the custom name
      // (The custom name from mailRecipientOtherName is only used for address.name)
      // For private person: use representant from array if available
      const rep = representants?.[0]
      if (rep) {
        // Build SSN for representant from birthDate and ssn
        const repIdentificationNumber = buildIdentificationNumber(rep.birthDate, rep.ssn)
        const phoneNumber = parseToPhoneCollectionModel(rep.phone, true)

        return {
          firstName: rep.firstName,
          lastName: rep.lastName,
          email: rep.email?.trim() ? rep.email.trim() : null,
          phoneNumber,
          socialSecurityNumber: repIdentificationNumber.identificationNumber?.number ?? null,
          birthDate: formatDateToISO(rep.birthDate),
        }
      }
      // For organization/sameie or fallback: use stepData.firstName/lastName (representant fields) or forest owner
      const phoneNumber = parseToPhoneCollectionModel(stepData.phone, true)

      return {
        firstName: stepData.firstName,
        lastName: stepData.lastName,
        email: stepData.email?.trim() ? stepData.email.trim() : null,
        phoneNumber,
        socialSecurityNumber: forestOwnerIdentificationNumber?.number ?? null,
        birthDate: formatDateToISO(stepData.birthDate),
      }
    }
    default: {
      // Fallback to forest owner name
      const phoneNumber = parseToPhoneCollectionModel(stepData.phone, true)

      return {
        firstName: stepData.firstName,
        lastName: stepData.lastName,
        email: stepData.email?.trim() ? stepData.email.trim() : null,
        phoneNumber,
        socialSecurityNumber: forestOwnerIdentificationNumber?.number ?? null,
        birthDate: formatDateToISO(stepData.birthDate),
      }
    }
  }
}

/**
 * Builds address data object from step data
 */
function buildAddressData(
  stepData: RegisterFormType['step1Person'] | RegisterFormType['step1Organization'],
): {
  streetName: string
  houseNumber: string
  postalCode: string
  city: string
  country: string
} {
  return {
    streetName: stepData.address,
    houseNumber: stepData.houseNumber,
    postalCode: stepData.postcode,
    city: stepData.city,
    country: stepData.country,
  }
}

/**
 * Builds address.name based on mail recipient selection
 */
function buildAddressName(
  mailRecipient: string,
  stepData: RegisterFormType['step1Person'] | RegisterFormType['step1Organization'],
  mailRecipientOtherName?: string | null,
  representantFirstName?: string | null,
  representantLastName?: string | null,
  organizationName?: string,
): string {
  if (mailRecipient === 'other' && mailRecipientOtherName?.trim()) {
    return mailRecipientOtherName.trim()
  }

  if (mailRecipient === 'representative') {
    if (representantFirstName && representantLastName) {
      return `${representantFirstName} ${representantLastName}`.trim()
    }
    // Fallback to forest owner/representant name if representant name parts not provided
    return `${stepData.firstName} ${stepData.lastName}`.trim()
  }

  if (mailRecipient === 'forestOwner') {
    // For organization/sameie types, when forestOwner is selected, it means "send to organization/sameie"
    // So if organizationName is provided, use it. Otherwise, use forest owner's name (for private person)
    if (organizationName?.trim()) {
      return organizationName.trim()
    }
    // For private person: always use forest owner's firstName and lastName
    return `${stepData.firstName} ${stepData.lastName}`.trim()
  }

  if (mailRecipient === 'organization') {
    // For organization mail recipient (only used in private person), use organization name if available
    if (organizationName?.trim()) {
      return organizationName.trim()
    }
    return `${stepData.firstName} ${stepData.lastName}`.trim()
  }

  // Default fallback
  return `${stepData.firstName} ${stepData.lastName}`.trim()
}

/**
 * Builds careOf field from address owner checkbox
 */
function buildCareOf(
  isAddressOwner?: boolean,
  addressOwnerName?: string | null,
): string | undefined {
  return isAddressOwner && addressOwnerName ? addressOwnerName : undefined
}

/**
 * Builds organization identification number data
 */
function buildOrganizationIdentificationNumber(
  organizationNumber: string,
  organizationType?: { code: string; description: string },
): {
  type: 'OrganizationNumber'
  number: string
  organizationType?: { code: string; description: string }
} | null {
  return organizationNumber
    ? {
        type: 'OrganizationNumber' as const,
        number: organizationNumber,
        organizationType,
      }
    : null
}

interface ContactPersonSource {
  firstName: string
  lastName: string
  email: string | null
  phoneNumber: PhoneCollectionModel | null
  socialSecurityNumber: string | null
  birthDate?: string
}

/**
 * Maps contact person data to the request model.
 * Known gap: birthDate may be undefined here while the backend requires it. The key is kept
 * as-is (omitted when serialized) so the payload is unchanged; the assertion only records the gap.
 */
function toContactPersonRequest(data: ContactPersonSource): ContactPersonModel {
  return {
    name: { firstName: data.firstName, lastName: data.lastName },
    email: data.email,
    phoneNumber: data.phoneNumber,
    socialSecurityNumber: data.socialSecurityNumber,
    birthDate: data.birthDate as string,
  }
}

/**
 * Builds contact person from representant for private person
 */
function buildContactPersonFromRepresentant(representant: RepresentantFormData): {
  firstName: string
  lastName: string
  email: string | null
  phoneNumber: PhoneCollectionModel | null
  socialSecurityNumber: string | null
  birthDate?: string
} {
  const repIdentificationNumber = buildIdentificationNumber(
    representant.birthDate,
    representant.ssn,
  )
  return {
    firstName: representant.firstName,
    lastName: representant.lastName,
    email: representant.email?.trim() ? representant.email.trim() : null,
    phoneNumber: parseToPhoneCollectionModel(representant.phone, true),
    socialSecurityNumber: repIdentificationNumber.identificationNumber?.number ?? null,
    birthDate: formatDateToISO(representant.birthDate),
  }
}

/**
 * Builds operating organization object
 */
export function buildOperatingOrganization(
  stepData: RegisterFormType['step1Person'] | RegisterFormType['step1Organization'],
  organizationNameOverride?: string,
): OperatingOrganizationModel | undefined | null {
  if (stepData.selectedOrganization) {
    const orgNumber = stepData.selectedOrganization.orgNumber
    const name = organizationNameOverride ?? stepData.selectedOrganization.name

    return {
      name,
      organizationNumber: orgNumber,
    }
  }

  return undefined
}

/**
 * Maps private person registration data to CreateForestOwnerRequest
 */
export function mapPrivatePersonPayload(
  formData: Pick<RegisterFormType, 'step1Person'>,
  tenantId: string,
): CreateForestOwnerRequest {
  const stepData = formData.step1Person

  // Parse phone number if provided
  const apiPhone = parseToPhoneNumberModel(stepData.phone, true)

  // Build identification number and birthDate
  const { identificationNumber, birthDate } = buildIdentificationNumber(
    stepData.birthDate,
    stepData.ssn,
  )

  const representants =
    (
      formData as RegisterFormType & {
        privatePersonStep?: { representants?: RepresentantFormData[] }
      }
    ).privatePersonStep?.representants ?? stepData.representants
  const representant = representants.length > 0 ? representants[0] : null

  const contactPersonData = representant ? buildContactPersonFromRepresentant(representant) : null

  const contactPerson: CreateForestOwnerRequest['contactPerson'] | null = contactPersonData
    ? toContactPersonRequest(contactPersonData)
    : null

  const careOf = buildCareOf(stepData.isAddressOwner, stepData.addressOwnerName)
  const addressData = buildAddressData(stepData)
  const topLevelName: NameModel = {
    firstName: stepData.firstName,
    lastName: stepData.lastName,
  }

  // For private person: only pass organizationName when mailRecipient is 'organization'
  // When 'forestOwner' is selected, we should always use forest owner's name, not organization name
  const organizationName =
    stepData.mailRecipient === 'organization' ? stepData.selectedOrganization?.name : undefined

  const addressName = buildAddressName(
    stepData.mailRecipient,
    stepData,
    stepData.mailRecipientOtherName,
    representant?.firstName,
    representant?.lastName,
    organizationName,
  )

  // Build operating organization
  const operatingOrganization = buildOperatingOrganization(stepData)

  const requestData: CreateForestOwnerRequest = {
    tenantId,
    name: topLevelName,
    email: stepData.email?.trim() ? stepData.email.trim() : null,
    address: {
      address: addressData,
      careOf,
      name: addressName,
    },
    mobilePhone: apiPhone ?? null,
    contactPerson,
    forestOwnerGroupNumber: null,
    identificationNumber,
    bankAccountNumber: stepData.accountNumber?.trim() ? stepData.accountNumber.trim() : null,
    operatingOrganization,
    forestOwnerType: 'Private',
  }

  if (birthDate) {
    requestData.birthDate = birthDate
  }

  if (stepData.phone && apiPhone) {
    requestData.landlinePhone = apiPhone
  }

  return requestData
}

/**
 * Maps Organization registration data to CreateForestOwnerRequest
 */
export function mapOrganizationPayload(
  formData: Pick<RegisterFormType, 'step1Organization'>,
  tenantId: string,
  organizationType?: { code: string; description: string },
  organizationNumber?: string,
): CreateForestOwnerRequest {
  const stepData = formData.step1Organization

  const finalOrganizationNumber =
    stepData.selectedOrganization?.orgNumber ?? organizationNumber ?? ''

  const apiPhone = parseToPhoneNumberModel(stepData.phone, true)

  const representantIdentificationNumber = buildIdentificationNumber(
    stepData.birthDate,
    stepData.ssn,
  )

  const contactPersonData = buildContactPerson(
    stepData.mailRecipient,
    stepData,
    [],
    representantIdentificationNumber.identificationNumber,
  )

  const contactPerson: CreateForestOwnerRequest['contactPerson'] =
    toContactPersonRequest(contactPersonData)

  const careOf = buildCareOf(stepData.isAddressOwner, stepData.addressOwnerName)
  const addressData = buildAddressData(stepData)
  const operatingOrg = buildOperatingOrganization(stepData, stepData.organizationName)
  const identificationNumberData = buildOrganizationIdentificationNumber(
    finalOrganizationNumber,
    organizationType,
  )

  const orgNameTrimmed = stepData.organizationName.trim()
  const nameData: NameModel =
    orgNameTrimmed !== ''
      ? { firstName: orgNameTrimmed, lastName: null }
      : { firstName: stepData.firstName, lastName: stepData.lastName }

  const addressName = buildAddressName(
    stepData.mailRecipient,
    stepData,
    stepData.mailRecipientOtherName,
    stepData.firstName,
    stepData.lastName,
    orgNameTrimmed,
  )

  const requestData: CreateForestOwnerRequest = {
    tenantId,
    name: nameData,
    email: stepData.email?.trim() ? stepData.email.trim() : null,
    address: {
      address: addressData,
      careOf,
      name: addressName,
    },
    mobilePhone: apiPhone ?? null,
    contactPerson,
    forestOwnerGroupNumber: null,
    identificationNumber: identificationNumberData,
    bankAccountNumber: stepData.accountNumber?.trim() ? stepData.accountNumber.trim() : null,
    operatingOrganization: operatingOrg,
    forestOwnerType: 'Organization',
  }

  // Include birthDate from representant when available
  const birthDate = formatDateToISO(stepData.birthDate)
  if (birthDate) {
    requestData.birthDate = birthDate
  }

  if (stepData.phone && apiPhone) {
    requestData.landlinePhone = apiPhone
  }

  return requestData
}

/**
 * Maps Sameie/Shared ownership registration data to CreateForestOwnerRequest
 */
export function mapSameiePayload(
  formData: Pick<RegisterFormType, 'step1Organization'>,
  tenantId: string,
  organizationType?: { code: string; description: string },
  organizationNumber?: string,
): CreateForestOwnerRequest {
  const stepData = formData.step1Organization

  const finalOrganizationNumber =
    stepData.selectedOrganization?.orgNumber ?? organizationNumber ?? ''

  const apiPhone = parseToPhoneNumberModel(stepData.phone, true)

  const representantIdentificationNumber = buildIdentificationNumber(
    stepData.birthDate,
    stepData.ssn,
  )

  const contactPersonData = buildContactPerson(
    stepData.mailRecipient,
    stepData,
    [],
    representantIdentificationNumber.identificationNumber,
  )

  const contactPerson: CreateForestOwnerRequest['contactPerson'] =
    toContactPersonRequest(contactPersonData)

  const careOf = buildCareOf(stepData.isAddressOwner, stepData.addressOwnerName)
  const addressData = buildAddressData(stepData)
  const operatingOrg = buildOperatingOrganization(stepData, stepData.organizationName)
  const identificationNumberData = buildOrganizationIdentificationNumber(
    finalOrganizationNumber,
    organizationType,
  )

  const sameieNameTrimmed = stepData.organizationName.trim()
  const nameData: NameModel =
    sameieNameTrimmed !== ''
      ? { firstName: sameieNameTrimmed, lastName: null }
      : { firstName: stepData.firstName, lastName: stepData.lastName }

  const addressName = buildAddressName(
    stepData.mailRecipient,
    stepData,
    stepData.mailRecipientOtherName,
    stepData.firstName,
    stepData.lastName,
    sameieNameTrimmed,
  )

  const requestData: CreateForestOwnerRequest = {
    tenantId,
    name: nameData,
    email: stepData.email?.trim() ? stepData.email.trim() : null,
    address: {
      address: addressData,
      careOf,
      name: addressName,
    },
    mobilePhone: apiPhone ?? null,
    contactPerson,
    forestOwnerGroupNumber: null,
    identificationNumber: identificationNumberData,
    bankAccountNumber: stepData.accountNumber?.trim() ? stepData.accountNumber.trim() : null,
    operatingOrganization: operatingOrg,
    forestOwnerType: 'Partnership',
  }

  // Include birthDate from representant when available
  const birthDate = formatDateToISO(stepData.birthDate)
  if (birthDate) {
    requestData.birthDate = birthDate
  }

  if (stepData.phone && apiPhone) {
    requestData.landlinePhone = apiPhone
  }

  return requestData
}
